import React from 'react';
import belerenFont from '@/assets/fonts/beleren.ttf'
import mplantinFont from '@/assets/fonts/mplantin.ttf'
import goudyFont from '@/assets/fonts/goudy.ttf'
import { getImageUrl } from '@/lib/utils/image'

const fontFaces = `
  @font-face {
    font-family: 'Beleren';
    src: url(${belerenFont});
  }
  @font-face {
    font-family: 'MPlantin';
    src: url(${mplantinFont});
  }
  @font-face {
    font-family: 'Goudy';
    src: url(${goudyFont});
  }
`;

const rarityColors = {
  common: 'bg-slate-400',
  uncommon: 'bg-zinc-200',
  rare: 'bg-amber-400',
  mythic: 'bg-red-500'
};

export interface MTGCardData {
  title?: string;
  description?: string;
  imageUrl?: string;
  metadata: {
    [key: string]: any;
    language: string;
    playerName: string;
    type?: string;
    cardType?: string;
    manaCost?: string;
    flavorText?: string;
    rarity?: string;
    power?: string;
    toughness?: string;
  };
}

interface MTGCardProps {
  cardData: MTGCardData;
}

export const MTGCard: React.FC<MTGCardProps> = ({ cardData }) => {
  console.log('MTGLayout rendering:', cardData);

  // Ensure metadata is properly structured
  const metadata = typeof cardData.metadata === 'string' ? 
    JSON.parse(cardData.metadata) : cardData.metadata;

  // Ensure all required fields exist
  const safeMetadata = {
    ...metadata,
    type: metadata.type || metadata.cardType || 'Unknown',
    manaCost: metadata.manaCost || '',
    flavorText: metadata.flavorText || '',
    rarity: metadata.rarity || 'common',
    language: metadata.language || 'en',
    playerName: metadata.playerName || 'Anonymous'
  };

  const getRarityColor = (rarity?: string): string => {
    const normalizedRarity = rarity?.toLowerCase() || 'common';
    return rarityColors[normalizedRarity as keyof typeof rarityColors] || rarityColors.common;
  };

  const imageUrl = getImageUrl(cardData.imageUrl);

  return (
    <div className="w-80 min-h-[500px] relative overflow-hidden border-8 border-slate-800 bg-slate-800 font-serif" >
      {/* Title Bar */}
      <style>{fontFaces}</style>
      <div className="bg-gray-200 p-2 flex justify-between items-center border-b-2 border-black">
        <h2 className="text-lg font-bold text-black tracking-tight" style={{ fontFamily: 'Beleren' }}>
          {cardData.title || 'Untitled'}
        </h2>
        <div className="flex items-center space-x-1">
          {safeMetadata.manaCost?.split('').map((char: string, index: number) => (
            <span key={index} className="w-5 h-5 flex items-center justify-center bg-gray-300 rounded-full text-sm text-black">
              {char}
            </span>
          ))}
        </div>
      </div>

      {/* Card Art */}
      <div className="card-image">
        <img 
          src={imageUrl} 
          alt={cardData.title}
          className="w-full h-full object-cover"
        />
      </div>

      {/* Type Line */}
      <div className="bg-gray-200 p-2 flex justify-between items-center border-b-2 border-black">
        <span className="font-semibold capitalize text-black">{safeMetadata.type}</span>
        <div className={`w-4 h-4 rounded-full ${getRarityColor(safeMetadata.rarity)}`} />
      </div>

      {/* Text Box */}
      <div className="bg-gray-100 p-2 min-h-32 border-b-2 border-black text-black">
        <p className="whitespace-pre-wrap mb-2 text-sm tracking-wide" style={{ fontFamily: 'MPlantin' }}>{cardData?.description}</p>
        {safeMetadata.flavorText && (
          <p className="text-sm italic text-gray-600" style={{ fontFamily: 'Goudy' }}>{safeMetadata.flavorText}</p>
        )}
      </div>

      {/* Power/Toughness */}
      {safeMetadata.power && safeMetadata.toughness && (
        <div className="absolute bottom-8 right-2 bg-gray-200 px-1 rounded-lg border-2 border-black text-black flex items-center justify-center">
          <span className="font-bold text-sm">{safeMetadata.power}/{safeMetadata.toughness}</span>
        </div>
      )}

      {/* Footer */}
      <div className="bg-gray-200 p-1 text-xs flex justify-between items-center text-black">
        <span>{safeMetadata.playerName}</span>
        <span>{safeMetadata.rarity}</span>
        <span>{safeMetadata.language}</span>
      </div>
    </div>
  );
};

export default MTGCard;